import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TransformSelector from './TransformSelector';
import SkillEditor from './SkillEditor';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './TransformEditor.css';
import './transform-print.css';
import { saveTransform } from '../../utils/saveTransform';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TIERS = ['static', 'basic', 'advanced', 'expert', 'master', 'grandmaster', 'mythic'];

const TIER_COSTS = {
    static: 'SP Cost: Free',
    basic: 'SP Cost: Active 2(+3) - Passive: 5',
    advanced: 'SP Cost: Active 3(+5) Passive: 10',
    expert: 'SP Cost: Active 5(+10) Passive: 15',
    master: 'SP Cost: Active 10(+10) Passive: 20',
    grandmaster: 'SP Cost: Active 10(+20) Passive: 30',
    mythic: 'SP Cost: Active 20(+20) Passive: 40'
};

export default function TransformEditor({ token }) {
    const [transforms, setTransforms] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', skills: {} });
    const [activeNewSkillTier, setActiveNewSkillTier] = useState(null);
    const [printMode, setPrintMode] = useState(false);
    const [focusedSkill, setFocusedSkill] = useState(null);

    const fetchTransforms = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/transforms`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTransforms(res.data);
        } catch (err) {
            console.error('Failed to fetch transforms', err);
        }
    };

    useEffect(() => {
        fetchTransforms();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
                e.preventDefault();
                setPrintMode(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (printMode) {
            setTimeout(() => {
                window.print();
                setPrintMode(false);
            }, 100);
        }
    }, [printMode]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        try {
            const skillTiers = formData.skills || {};
            const allSkills = [];
            for (const [tierLabel, entries] of Object.entries(skillTiers)) {
                const tierIndex = ['static', 'basic', 'advanced', 'expert', 'master', 'grandmaster', 'mythic'].indexOf(tierLabel);
                if (tierIndex === -1) continue;
                for (const skill of entries) {
                    allSkills.push({
                        skillId: skill.skillId || undefined,
                        name: skill.name,
                        notes: skill.notes || '',
                        tier: tierIndex,
                        isPassive: !!skill.isPassive,
                        isDefaultUnlocked: !!skill.isDefaultUnlocked
                    });
                }
            }

            const transformToSave = {
                ...formData,
                skills: allSkills,
                transformId: selectedId || formData.transformId || undefined
            };

            const savedId = await saveTransform(transformToSave, token);
            toast.success(`Transform ${selectedId ? 'updated' : 'created'} successfully`);
            fetchTransforms();
            setSelectedId(savedId);
        } catch (err) {
            console.error('Failed to save transform', err);
            toast.error('Failed to save transform');
        }
    };


    const isCreatingNew = !selectedId && formData.name !== '';

    const setTierSkills = (tier, skills) => {
        setFormData((prev) => ({
            ...prev,
            skills: {
                ...prev.skills,
                [tier]: skills
            }
        }));
    };

    const moveSkill = (tier, index, direction) => {
        const tierIndex = TIERS.indexOf(tier);
        const newTierIndex = tierIndex + direction;
        if (newTierIndex < 0 || newTierIndex >= TIERS.length) return;

        const skillToMove = formData.skills[tier][index];
        const updatedCurrentTier = [...formData.skills[tier]];
        updatedCurrentTier.splice(index, 1);

        const newTier = TIERS[newTierIndex];
        const updatedNewTier = [...(formData.skills[newTier] || []), skillToMove];

        setFormData((prev) => ({
            ...prev,
            skills: {
                ...prev.skills,
                [tier]: updatedCurrentTier,
                [newTier]: updatedNewTier
            }
        }));
    };

    const removeSkill = (tier, index) => {
        const updatedTier = [...formData.skills[tier]];
        updatedTier.splice(index, 1);
        setTierSkills(tier, updatedTier);
    };

    const updateSkillField = (tier, index, field, value) => {
        const updated = [...formData.skills[tier]];
        updated[index] = { ...updated[index], [field]: value };
        setTierSkills(tier, updated);
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        const srcTier = source.droppableId;
        const destTier = destination.droppableId;
        const srcList = [...(formData.skills[srcTier] || [])];
        const [moved] = srcList.splice(source.index, 1);
        if (srcTier === destTier) {
            srcList.splice(destination.index, 0, moved);
            setTierSkills(srcTier, srcList);
        } else {
            const destList = [...(formData.skills[destTier] || [])];
            destList.splice(destination.index, 0, moved);
            setFormData(prev => ({
                ...prev,
                skills: {
                    ...prev.skills,
                    [srcTier]: srcList,
                    [destTier]: destList
                }
            }));
        }
    };

    const renderPrintView = () => (
        <div className="print-view">
            <div id="transformName">{formData.name}</div>
            <div id="transformDescription">{formData.description}</div>
            {TIERS.map((tier) =>
                    (formData.skills[tier]?.length > 0) && (
                        <div id={tier} className="skillTierContainer" key={tier}>
                            <span className="skillTierTitle">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                            <span className="skillCost">{TIER_COSTS[tier]}</span>
                            <ul className="skillList">
                                {formData.skills[tier].map((skill, index) => (
                                    <React.Fragment key={index}>
                                        <li
                                            className={`skillName ${tier === 'static' ? 'isStatic' : ''} ${tier !== 'static' && skill.isPassive ? 'isPassive' : ''} ${tier !== 'static' && (skill.isDefaultUnlocked ? 'isUnlocked' : 'isNotUnlocked')}`}
                                        >
                                            {skill.name}
                                        </li>
                                        {skill.notes && (
                                            <ul className="skillNotes">
                                                {skill.notes.split('\n').map((line, i) => (
                                                    <li key={i} className="skillNote">{line}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                    )
            )}
            <div id="legend">
                Legend:<br />
                † Passive skill<br />
                ☐ Locked skill<br />
                ☑ Unlocked skill<br />
                ⮚ Static skill
            </div>
        </div>
    );

    if (printMode) return renderPrintView();

    return (
        <div className="editor-container">
            <h2>Transform Editor</h2>
            <TransformSelector
                token={token}
                transforms={transforms}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                setFormData={setFormData}
            />
            {(isCreatingNew || selectedId) && (
                <>
                    <div className="editor-section">
                        <label>Name:</label>
                        <input name="name" value={formData.name} onChange={handleChange} />
                    </div>
                    <div className="editor-section">
                        <label>Description:</label>
                        <textarea name="description" value={formData.description} onChange={handleChange} />
                    </div>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className="editor-section">
                            {TIERS.map((tier) => (
                                <div key={tier} className="tier-block">
                                    <div className="tier-heading">
                                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                        <button
                                            className="add-skill-button"
                                            onClick={() => setActiveNewSkillTier(tier)}
                                            title="Add new skill"
                                        >
                                            ＋
                                        </button>
                                    </div>
                                    <Droppable droppableId={tier}>
                                        {(provided) => (
                                            <ul {...provided.droppableProps} ref={provided.innerRef} className="skill-list">
                                                {(formData.skills[tier] || []).map((skill, index) => (
                                                    <Draggable key={`${tier}-${index}`} draggableId={`${tier}-${index}`} index={index}>
                                                        {(provided) => (
                                                            <li
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="transform-skill-card"
                                                            >
                                                                <div className="move-skill-container">
                                                                    {tier !== 'static' && <button className="move-skill-up" onClick={() => moveSkill(tier, index, -1)}>↑</button>}
                                                                    <button onClick={() => removeSkill(tier, index)}>✕</button>
                                                                    {tier !== 'mythic' && <button className="move-skill-down" onClick={() => moveSkill(tier, index, 1)}>↓</button>}
                                                                </div>
                                                                <div className="toggle-container">
                                                                    <div
                                                                        className={`toggle ${skill.isDefaultUnlocked ? 'active' : ''}`}
                                                                        tabIndex="0"
                                                                        role="button"
                                                                        onClick={() => updateSkillField(tier, index, 'isDefaultUnlocked', !skill.isDefaultUnlocked)}
                                                                    >
                                                                        Default
                                                                    </div>
                                                                    <div
                                                                        className={`toggle ${skill.isPassive ? 'active' : ''}`}
                                                                        tabIndex="0"
                                                                        role="button"
                                                                        onClick={() => updateSkillField(tier, index, 'isPassive', !skill.isPassive)}
                                                                    >
                                                                        Passive
                                                                    </div>
                                                                </div>
                                                                <div className="input-container">
                                                                    <input
                                                                        className="styled-input skill-name-header"
                                                                        value={skill.name}
                                                                        onChange={(e) => updateSkillField(tier, index, 'name', e.target.value)}
                                                                        placeholder="Name"
                                                                    />
                                                                    {
                                                                        focusedSkill?.tier === tier && focusedSkill?.index === index ? (
                                                                            <textarea
                                                                                className="styled-input"
                                                                                autoFocus
                                                                                ref={(el) => {
                                                                                    if (el) {
                                                                                        const len = el.value.length;
                                                                                        el.setSelectionRange(len, len);
                                                                                    }
                                                                                }}
                                                                                value={skill.notes || ''}
                                                                                onChange={(e) => updateSkillField(tier, index, 'notes', e.target.value)}
                                                                                placeholder="Notes (each line becomes a bullet)"
                                                                                rows={3}
                                                                                onBlur={(e) => {
                                                                                    updateSkillField(tier, index, 'notes', e.target.value);
                                                                                    setFocusedSkill(null);
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        if (e.ctrlKey || e.metaKey) {
                                                                                            e.preventDefault();
                                                                                            const { selectionStart, selectionEnd, value } = e.target;
                                                                                            const newValue = value.slice(0, selectionStart) + '\n' + value.slice(selectionEnd);
                                                                                            updateSkillField(tier, index, 'notes', newValue);
                                                                                            setTimeout(() => {
                                                                                                e.target.selectionStart = e.target.selectionEnd = selectionStart + 1;
                                                                                            }, 0);
                                                                                        } else {
                                                                                            e.preventDefault();
                                                                                            updateSkillField(tier, index, 'notes', e.target.value);
                                                                                            e.target.blur();
                                                                                        }
                                                                                    }
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                className="styled-input formatted-notes"
                                                                                onClick={() => setFocusedSkill({ tier, index })}
                                                                            >
                                                                                {(skill.notes || '').split('\n').filter(line => line.trim() !== '').map((line, i) => (
                                                                                    <div key={i}>- {line}</div>
                                                                                ))}
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            </li>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </ul>
                                        )}
                                    </Droppable>
                                    {activeNewSkillTier === tier && (
                                        <SkillEditor
                                            skills={{ [tier]: formData.skills[tier] || [] }}
                                            setSkills={(updatedTier) => setTierSkills(tier, updatedTier[tier])}
                                            fixedTier={tier}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </DragDropContext>
                    <div className="editor-section">
                        <button onClick={handleSubmit}>{selectedId ? 'Update' : 'Create'} Transform</button>
                        <button onClick={() => setPrintMode(true)} style={{ marginLeft: '1rem' }}>Print View</button>
                    </div>
                </>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
}
